import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { services, media, contactInfo } from "@/i18n/translations";
import SEO from "@/components/SEO";
import { FadeUp, PageHeader } from "@/components/ui-primitives";
import AnimatedServiceIcon from "@/components/AnimatedServiceIcon";
import { trackEvent } from "@/lib/analytics";

const Services = () => {
  const { t, lang } = useLanguage();
  return (
    <>
      <SEO
        path="/services"
        title={lang === "ru" ? "Услуги яхтенного агентирования" : "Yacht Agency Services"}
        description={
          lang === "ru"
            ? "Девять ключевых яхтенных услуг в Турции — от Transit Log до консьержа."
            : "Nine core yacht agency services in Türkiye — from Transit Log clearance to concierge."
        }
        keywords="yacht agency services Turkey, yacht port clearance, berth reservation Turkey, yacht provisioning"
      />
      <PageHeader
        eyebrow={t.servicesPage.eyebrow}
        title={t.servicesPage.title}
        intro={t.servicesPage.intro}
        image="/assets/photos/services-hero.jpg"
        testId="services-header"
      />

      <section className="py-20 sm:py-28 px-6 sm:px-10 lg:px-16" data-testid="services-grid">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-navy/10">
          {services.map((s, i) => {
            return (
              <FadeUp key={s.slug} delay={i * 0.04}>
                <Link
                  to={`/services/${s.slug}`}
                  className="group block bg-bone hover:bg-cream transition-colors p-10 h-full"
                  data-testid={`service-card-${s.slug}`}
                >
                  <div className="flex items-start justify-between mb-10">
                    <AnimatedServiceIcon slug={s.slug} iconName={s.icon} size={32} />
                    <span className="font-display text-navy/20 text-2xl">
                      0{i + 1}
                    </span>
                  </div>
                  <h2 className="font-display text-3xl text-navy leading-tight mb-4 group-hover:text-brand transition-colors">
                    {s[lang].name}
                  </h2>
                  <p className="text-sm text-navy/70 leading-relaxed">{s[lang].short}</p>
                  <div className="mt-8 flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-brand/80 group-hover:gap-3 transition-all">
                    {t.common.viewService} <ArrowRight size={12} />
                  </div>
                </Link>
              </FadeUp>
            );
          })}
        </div>
      </section>

      {/* Premium "wow" CTA — "before the lines come ashore" */}
      <section
        className="relative overflow-hidden bg-navy text-white px-6 sm:px-10 lg:px-16 py-28 sm:py-36"
        data-testid="services-cta"
      >
        {/* Background image with deep navy overlay */}
        <div className="absolute inset-0 -z-0">
          <img
            src={media.heroMain}
            alt=""
            className="w-full h-full object-cover opacity-25"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/95 to-navy/85" />
        </div>

        {/* Vertical gold rule */}
        <div className="absolute top-0 bottom-0 left-6 sm:left-10 lg:left-16 w-px bg-gradient-to-b from-transparent via-gold/40 to-transparent" aria-hidden="true" />

        <div className="relative z-10 max-w-5xl ml-6 sm:ml-10">
          <FadeUp>
            <div className="flex items-center gap-3 mb-8">
              <span className="w-2 h-2 rounded-full bg-brand animate-pulse" aria-hidden="true" />
              <span className="text-[10px] tracking-[0.35em] uppercase text-gold/85 font-semibold">
                {t.servicesPage.ctaEyebrow}
              </span>
            </div>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h2 className="font-display italic text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight pb-[0.15em]">
              <span className="text-brand">{t.servicesPage.ctaTitle.split("—")[0]?.trim() || t.servicesPage.ctaTitle}</span>
              {t.servicesPage.ctaTitle.includes("—") && (
                <>
                  <br />
                  <span className="text-white not-italic font-display">
                    {t.servicesPage.ctaTitle.split("—").slice(1).join("—").trim()}
                  </span>
                </>
              )}
            </h2>
          </FadeUp>

          <FadeUp delay={0.18}>
            <p className="mt-10 max-w-2xl text-lg leading-[1.7] text-white/80">
              {t.servicesPage.ctaBody}
            </p>
          </FadeUp>

          <FadeUp delay={0.26}>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 sm:gap-5 sm:items-center">
              <Link
                to="/contact"
                onClick={() => trackEvent("request_support_click", { surface: "services_cta" })}
                className="inline-flex items-center justify-center gap-3 bg-brand hover:bg-brand-deep text-white px-8 py-4 text-[11px] tracking-[0.3em] uppercase font-semibold transition-all hover:gap-4"
                data-testid="services-cta-primary"
              >
                {t.servicesPage.ctaPrimary} <ArrowRight size={14} />
              </Link>
              <a
                href={`https://wa.me/${contactInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_click", { surface: "services_cta" })}
                className="inline-flex items-center justify-center gap-3 border border-white/25 hover:border-gold text-white px-8 py-4 text-[11px] tracking-[0.3em] uppercase font-semibold transition-colors"
                data-testid="services-cta-whatsapp"
              >
                <MessageCircle size={14} strokeWidth={1.6} />
                {t.servicesPage.ctaWhatsapp}
              </a>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default Services;
